import { FC, useCallback, useEffect, useReducer, useState } from "react";
import MenuBasicInfoEditSection from "components/menu/ui/MenuBasicInfoEditSection";
import EditSection from "components/common/ui/EditSection";
import MenuEditActionButtons from "components/menu/ui/MenuEditActionButtons";
import MenuContentsEditSection from "components/menu/ui/MenuContentsEditSection";
import {
  defaultMenuSectionDisplay,
  MenuContentActions,
  menuContentsReducer,
} from "models/menu/menuContentsReducer";
import { v4 as uuidv4 } from "uuid";
import {
  FoodItem,
  Menu,
  MenuCreateDeep,
  type MenuOptionGroupCreateDeep,
  MenuOptionGroupUpdateDeepInBatch,
  MenuUpdateDeep,
} from "client/jspPlatformExperiment";
import jspPlatformApi from "client/portals";
import { useAppDispatch, useAppSelector } from "hooks/useReduxStore";
import menuDetailSelector from "store/selectors/menu/menuDetailSelector";
import { usePromptSnackBar } from "components/common/ui/PromptSnackBarProvider";
import { useNavigate } from "react-router-dom";
import { menuDetailActions } from "store/slices/menu/menuDetailSlice";
import { menuOptionsActions } from "store/slices/menu/menuOptionsSlice";
import { rawMenuEditReadQuery } from "utils/menu/menu/menuQueryUtils";
import { getMenuOptionGroupFromDisplay } from "utils/menu/menu/menuDisplayToDataUtils";
import {
  countItemsMissingFood,
  filterSkippableMenuItems,
} from "utils/menu/menu/menuDisplayValidation";
import Routes from "models/routes";

interface MenuEditFormsProps {
  foodItemOptions: FoodItem[];
  initialName?: string;
  onSaveSuccess?: (menu: Menu) => void;
  hideSaveAsNew?: boolean;
  hideDelete?: boolean;
}

const MenuEditForms: FC<MenuEditFormsProps> = ({
  foodItemOptions,
  initialName,
  onSaveSuccess,
  hideSaveAsNew = false,
  hideDelete = false,
}) => {
  const dispatch = useAppDispatch();
  const promptSnackBar = usePromptSnackBar();
  const navigate = useNavigate();
  const editingMenuData = useAppSelector(menuDetailSelector.editingMenu);
  const specialDateInfo = useAppSelector(menuDetailSelector.specialDateInfo);

  const [menuBasicInfo, setMenuBasicInfo] = useState<Omit<Menu, "sections">>({
    id: 0,
    name: initialName,
    publish_prior_to_serving_time: "PT1H",
    cap_finalization_prior_to_serving_time: "P1D",
    user_groups: [],
  });
  const [menuContents, dispatchMenuContents] = useReducer(menuContentsReducer, {
    [uuidv4()]: defaultMenuSectionDisplay(),
  });

  /**
   * Use effect to sync editing menu data
   */
  useEffect(() => {
    if (editingMenuData) {
      setMenuBasicInfo(editingMenuData);
      dispatchMenuContents({
        type: MenuContentActions.LOAD_FROM_MENU_DATA,
        UUIDPath: "",
        menuData: editingMenuData,
      });
    }
  }, [editingMenuData]);

  /**
   * Save as new menu
   */
  const handleCreateMenu = useCallback(() => {
    const missingFoodCount = countItemsMissingFood(menuContents);
    if (missingFoodCount > 0) {
      const baseMessage =
        "Cannot leave the food selection blank when its side is not empty.";
      promptSnackBar.setPromptProps({
        isOpen: true,
        message:
          missingFoodCount === 1
            ? baseMessage
            : `${baseMessage} (${missingFoodCount} items)`,
        severity: "error",
        autoHeightDuration: 5000,
      });
      return;
    }
    const filteredMenuContents = filterSkippableMenuItems(menuContents);
    const toCreateMenu: MenuCreateDeep = {
      ...menuBasicInfo,
      user_group_ids: menuBasicInfo.user_groups.map((group) => group.id),
      sections: Object.values(filteredMenuContents).map(
        (displaySection): MenuOptionGroupCreateDeep => {
          const section = getMenuOptionGroupFromDisplay(displaySection, true);
          // Ensure required properties are properly defined
          if (section.name === null || section.name === undefined) {
            section.name = ""; // Provide default
          }
          return section as MenuOptionGroupCreateDeep;
        },
      ),
    };
    // Call Jasper platform API to save new menu
    jspPlatformApi()
      .menu.postMenuMeal({ body: toCreateMenu })
      .then((res) => {
        const menu = res.data ?? null;
        if (!menu) {
          return;
        }
        // Push success message
        promptSnackBar.setPromptProps({
          isOpen: true,
          message: `Create menu success.`,
          severity: "success",
          autoHeightDuration: 5000,
        });
        // The response comes back with display names auto-filled by the
        // server: cache it for view surfaces (menu options), but never feed
        // it to the edit form — the navigation below remounts the edit page,
        // which reloads the raw stored menu.
        dispatch(menuOptionsActions.setMenuOptions([menu]));

        // If an external save handler is provided, delegate post-save behavior
        // (used by callers that embed this form, e.g. the create-in-loop dialog).
        if (onSaveSuccess) {
          onSaveSuccess(menu);
          return;
        }

        // If there's special date info, add special date event to menu loop
        if (
          specialDateInfo &&
          specialDateInfo.targetMenuLoopId &&
          specialDateInfo.newSpecialDateCreate
        ) {
          // Call Jasper platform API to add a new special date event to the target menu loop
          jspPlatformApi()
            .menuLoop.postMenuLoopByMenuLoopIdSkipDate({
              path: { menu_loop_id: specialDateInfo.targetMenuLoopId },
              body: [specialDateInfo.newSpecialDateCreate],
            })
            .then(() => {
              promptSnackBar.setPromptProps({
                isOpen: true,
                message: `Special date added to menu loop ${specialDateInfo.targetMenuLoopId}.`,
                severity: "success",
                autoHeightDuration: 5000,
              });
              navigate(-1);
            })
            .catch(() => {
              promptSnackBar.setPromptProps({
                isOpen: true,
                message:
                  "Menu created successfully, but failed to add special dates to menu loops.",
                severity: "warning",
                autoHeightDuration: 5000,
              });
            });
        } else {
          navigate(`${Routes.MENU_EDIT}/${menu.id}`, {
            replace: true,
            state: { editingMenuId: menu.id },
          });
        }
      })
      .catch(() => {});
  }, [
    menuBasicInfo,
    menuContents,
    promptSnackBar,
    dispatch,
    specialDateInfo,
    navigate,
    onSaveSuccess,
  ]);

  /**
   * Delete menu
   */
  const handleDeleteMenu = useCallback(
    (editingMenuId: number) => () => {
      if (editingMenuId !== 0) {
        jspPlatformApi()
          .menu.deleteMenuMealByMenuId({ path: { menu_id: editingMenuId } })
          .then(() => {
            promptSnackBar.setPromptProps({
              isOpen: true,
              message: `Delete menu (ID: ${editingMenuId}) success.`,
              severity: "success",
              autoHeightDuration: 5000,
            });
            navigate(-1);
          })
          .catch(() => {});
      }
    },
    [navigate, promptSnackBar],
  );

  /**
   * Update menu
   */
  const handleUpdateMenu = useCallback(
    (editingMenuId: number) => {
      if (editingMenuId === 0) {
        return handleCreateMenu;
      }
      return () => {
        const missingFoodCount = countItemsMissingFood(menuContents);
        if (missingFoodCount > 0) {
          const baseMessage =
            "Cannot leave the food selection blank when its side is not empty.";
          promptSnackBar.setPromptProps({
            isOpen: true,
            message:
              missingFoodCount === 1
                ? baseMessage
                : `${baseMessage} (${missingFoodCount} items)`,
            severity: "error",
            autoHeightDuration: 5000,
          });
          return;
        }
        const filteredMenuContents = filterSkippableMenuItems(menuContents);
        const toDeepUpdateMenu: MenuUpdateDeep = {
          ...menuBasicInfo,
          sections: Object.values(filteredMenuContents).map(
            (
              displaySection,
            ): MenuOptionGroupCreateDeep | MenuOptionGroupUpdateDeepInBatch =>
              getMenuOptionGroupFromDisplay(displaySection),
          ),
          user_group_ids: menuBasicInfo.user_groups.map((group) => group.id),
        };
        // Update the menu basic information
        jspPlatformApi()
          .menu.putMenuMealByMenuId({
            path: { menu_id: editingMenuId },
            body: toDeepUpdateMenu,
          })
          .then((res) => {
            const menu = res.data ?? null;
            if (menu) {
              promptSnackBar.setPromptProps({
                isOpen: true,
                message: `Update menu (ID: ${editingMenuId}) success.`,
                severity: "success",
                autoHeightDuration: 5000,
              });
              // The response comes back with display names auto-filled by
              // the server: cache it for view surfaces (menu options), but
              // never feed it to the edit form.
              dispatch(menuOptionsActions.setMenuOptions([menu]));
              if (onSaveSuccess) {
                // Embedding callers (create-in-loop dialog) close this form
                // on success, so no editing state needs refreshing.
                onSaveSuccess(menu);
                return;
              }
              // Repopulate the edit form from the raw stored menu so the
              // auto-filled display names never become editing state (the
              // next save would persist them as real values). The reload
              // also picks up ids assigned to newly created sections/items.
              jspPlatformApi()
                .menu.getMenuMealByMenuId({
                  path: { menu_id: editingMenuId },
                  query: rawMenuEditReadQuery,
                })
                .then((rawRes) => {
                  dispatch(
                    menuDetailActions.setMenuDetail(rawRes.data ?? null),
                  );
                })
                .catch(() => {});
            }
          })
          .catch(() => {});
      };
    },
    [
      dispatch,
      handleCreateMenu,
      menuBasicInfo,
      menuContents,
      onSaveSuccess,
      promptSnackBar,
    ],
  );

  return (
    <>
      <EditSection>
        <MenuEditActionButtons
          onCreate={handleCreateMenu}
          onUpdate={
            specialDateInfo
              ? handleCreateMenu
              : handleUpdateMenu(editingMenuData ? editingMenuData.id : 0)
          }
          onDelete={handleDeleteMenu(
            editingMenuData && !specialDateInfo ? editingMenuData.id : 0,
          )}
          hideSaveAsNew={hideSaveAsNew}
          hideDelete={hideDelete}
        />
      </EditSection>
      <EditSection header="Basic Information">
        <MenuBasicInfoEditSection
          menuBasicInfo={menuBasicInfo}
          setMenuBasicInfo={setMenuBasicInfo}
        />
      </EditSection>
      <EditSection header="Menu Items">
        <MenuContentsEditSection
          menuContents={menuContents}
          dispatchMenuContents={dispatchMenuContents}
          foodItemOptions={foodItemOptions}
        />
      </EditSection>
      <MenuEditActionButtons
        onCreate={handleCreateMenu}
        onUpdate={
          specialDateInfo
            ? handleCreateMenu
            : handleUpdateMenu(editingMenuData ? editingMenuData.id : 0)
        }
        onDelete={handleDeleteMenu(
          editingMenuData && !specialDateInfo ? editingMenuData.id : 0,
        )}
        hideSaveAsNew={hideSaveAsNew}
        hideDelete={hideDelete}
      />
    </>
  );
};

export default MenuEditForms;
